import { PhotoGeofenceResult, PhotoLocation } from '@/types/photo-check.type';

const mapLink = (l: { lat: number; lng: number }) =>
  `https://www.google.com/maps?q=${l.lat},${l.lng}`;

/**
 * Where a photo was taken: compared with the checklist's area when one is set (inside, outside and how far),
 * otherwise just the spot on a map. "No location" when the phone did not share it.
 * compact: a short label for thumbnails. Otherwise a sentence, with a map link when the location is known.
 */
export default function PhotoGeofenceBadge({
  geofence,
  location,
  compact
}: {
  geofence?: PhotoGeofenceResult | null;
  location?: PhotoLocation | null;
  compact?: boolean;
}) {
  // No area set on the checklist: just where the photo was taken, or that it is unknown
  if (!geofence) {
    if (compact) {
      return (
        <span className="text-[10px] text-black/50">
          {location ? '📍 Location saved' : '📍 No location'}
        </span>
      );
    }
    return (
      <div className="rounded px-2 py-1 text-xs bg-black/5 text-black/70">
        {location ? (
          <>
            📍 Location saved (within {location.accuracy} m) ·{' '}
            <a
              href={mapLink(location)}
              target="_blank"
              rel="noreferrer"
              className="underline"
              onClick={e => e.stopPropagation()}
            >
              See on map
            </a>
          </>
        ) : (
          '📍 No location: the phone did not share where this photo was taken'
        )}
      </div>
    );
  }
  const place = geofence.label || 'the area';

  if (compact) {
    const text =
      geofence.status === 'inside'
        ? '📍 In area'
        : geofence.status === 'outside'
        ? `⚠ ${formatDistance(geofence.distance_m)} away`
        : '📍 No location';
    const className =
      geofence.status === 'inside'
        ? 'text-green-800'
        : geofence.status === 'outside'
        ? 'text-red-700 font-medium'
        : 'text-amber-800';
    return <span className={'text-[10px] ' + className}>{text}</span>;
  }

  const text =
    geofence.status === 'inside'
      ? `📍 Taken at ${place} (${formatDistance(
          geofence.distance_m
        )} from its centre, area ${geofence.radius_m} m)`
      : geofence.status === 'outside'
      ? `⚠ Taken ${formatDistance(
          geofence.distance_m
        )} from ${place}, outside its ${geofence.radius_m} m area`
      : `📍 No location: the phone did not share where this photo was taken`;
  const className =
    geofence.status === 'inside'
      ? 'bg-green-50 text-green-800'
      : geofence.status === 'outside'
      ? 'bg-red-50 text-red-800'
      : 'bg-amber-50 text-amber-800';

  return (
    <div className={'rounded px-2 py-1 text-xs ' + className}>
      {text}
      {location && (
        <>
          {' · '}
          <a
            href={mapLink(location)}
            target="_blank"
            rel="noreferrer"
            className="underline"
            onClick={e => e.stopPropagation()}
          >
            See on map
          </a>
        </>
      )}
    </div>
  );
}

function formatDistance(metres?: number) {
  if (metres === undefined) return '?';
  return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${metres} m`;
}
